import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigApiService } from '@api-client/config.api';
import { CustomerUserApiService } from '@api-client/customer-user.api';
import { AppService } from '@core/services/app.service';
import { AuthentificationService } from '@core/services/authentification/authentification.service';
import { StorageKey, StorageService } from '@core/services/storage/storage.service';
import { UserService } from '@core/services/user/user.service';
import { LoaderDirective } from '@shared/directives/loader/loader.directive';
import { NotificationService } from '@shared/services/notification.service';
import { ButtonComponent } from '@ui/button/button.component';
import { AutoCompleteComponent } from '@ui/form-controls/auto-complete/auto-complete.component';
import { BaseModalComponent } from '@ui/modal/base-modal.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged, filter, lastValueFrom, take, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-impersonation-modal',
  imports: [ReactiveFormsModule, ModalComponent, AutoCompleteComponent, ButtonComponent, LoaderDirective],
  templateUrl: './impersonation-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationModalComponent extends BaseModalComponent implements OnInit {
  protected readonly customerUserApiService = inject(CustomerUserApiService);
  protected readonly userService = inject(UserService);
  private readonly configApiService = inject(ConfigApiService);
  private readonly appApiService = inject(AppService);
  private readonly notificationService = inject(NotificationService);
  private readonly authentificationService = inject(AuthentificationService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private userName = StorageService.getUsername();
  private groupControl = new FormControl({ NOM: StorageService.getUserGroup() }, Validators.required);
  private userNameControl = new FormControl({ value: this.userName, disabled: !this.userName }, Validators.required);
  protected impersonationForm: FormGroup;
  protected loading = signal(false);
  protected loadingUser = signal(false);

  private getGroupList(): void {
    if (!this.customerUserApiService.userGroupList()?.length) {
      this.loading.set(true);
      this.customerUserApiService.loadGroups().subscribe({
        next: () => this.loading.set(false),
        error: () => this.loading.set(false),
      });
    }
  }

  private getUserList() {
    this.loadingUser.set(true);
    this.customerUserApiService.loadUsers(this.groupControl.value['NOM']).subscribe({
      next: () => this.loadingUser.set(false),
      error: () => this.loadingUser.set(false),
    });
  }

  ngOnInit(): void {
    this.getGroupList();

    if (StorageService.getUserGroup()?.length) {
      this.getUserList();
    }

    this.impersonationForm = new FormGroup({
      group: this.groupControl,
      username: this.userNameControl,
    });

    this.groupControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap((value) => {
          this.userNameControl.patchValue(null);
          if (value) {
            this.userNameControl.enable();
          } else {
            this.userNameControl.disable();
          }
        }),
        filter((value) => !!value),
      )
      .subscribe(() => this.getUserList());
  }

  confirm() {
    const group = this.groupControl.value['NOM'];
    const user_name = this.userNameControl.value['NOM'];
    this.configApiService
      .setDemoGroup({
        group_name_to_set_demo: group,
        user_name_to_set_demo: user_name,
      })
      .pipe(take(1))
      .subscribe({
        next: async (clientHasPortal: boolean) => {
          this.appApiService.clientHasPortal.set(clientHasPortal);
          StorageService.set(StorageKey.UserGroup, group);
          StorageService.set(StorageKey.Username, user_name);
          this.authentificationService.authUser.update((user) => cloneDeep(user));
          await lastValueFrom(this.appApiService.loadOptions());
          this.notificationService.success('customer-account.state-select-success');
          this.changeDetectorRef.detectChanges();
          this.close(true);
        },
        error: (error) => console.error(error),
      });
  }
}
